// Markdown to safe HTML. markdown-it as today's pages set it up (GFM tables,
// strikethrough, links found in the text, task lists), with raw HTML shown as
// text: the parts and the record are written by agents as much as by people,
// and a text must not draw the app's own controls. A fenced block is
// highlighted (highlight.js) and boxed; a `mermaid` one is a placeholder the
// view draws; headings carry slug ids; an issue key is a link, with a chip
// of its state where the page knows it. The renderer emits no button: the
// view (MarkdownView) puts Copy, Run and the zoom in the blocks, so the
// sanitiser stays as strict as for plain text. DOMPurify has the last word.
import MarkdownIt from 'markdown-it'
import type { StateCore, Token } from 'markdown-it'
import taskLists from 'markdown-it-task-lists'
import { highlightCode } from './highlight'
import { exactIssueKey, findIssueKeys, issueKeyFromHref, stateName } from './issues'
import { sanitizeHtml } from './sanitize'
import type { IssueRef, ResolveIssue } from './types'

const ENV_KEY = 'poulpe'

function contextOf(state: StateCore): RenderOptions {
  return (state.env[ENV_KEY] as RenderOptions | undefined) ?? {}
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  )
}

const markdown = new MarkdownIt('default', { html: false, linkify: true, typographer: false, breaks: false })
// `example.com` stays text; `https://example.com` and `www.example.com` become links.
markdown.linkify.set({ fuzzyLink: false })

export interface RenderOptions {
  resolveUrl?: (url: string, kind: 'link' | 'image') => string
  /** The key names issue keys are read in; the resolver's own when it has them. */
  prefixes?: readonly string[]
  /** Looks an issue up by its key: a link to it gets a chip of its state. */
  resolveIssue?: ResolveIssue
  /**
   * Load no image from another server: an address is all it takes for a
   * text to tell that server something. The image's own words show in its
   * place. An agent's reply is rendered so, as today's transcript does.
   */
  localImagesOnly?: boolean
  /** Imported content addresses that now belong to this board. */
  fileHashes?: ReadonlySet<string>
  /**
   * After a link whose words are not its address, the address in brackets,
   * so what a link says cannot hide where it leads. An issue's link and a
   * bare address need none. A question an agent asks is rendered so.
   */
  linkTargets?: boolean
}

const IMAGE_BASE = new URL('https://transcript.invalid/')
const RASTER_DATA = /^data:image\/(?:png|jpe?g|gif|webp);base64,/i

/** Resolve local images once, and emit paths whose spelling cannot select another origin. */
function localImageSource(source: string): string | null {
  if (source.trim() === '') return null
  try {
    const url = new URL(source, IMAGE_BASE)
    if (url.protocol === 'data:') return RASTER_DATA.test(url.href) ? url.href : null
    if (url.protocol !== IMAGE_BASE.protocol || url.origin !== IMAGE_BASE.origin) return null
    // The dot also keeps a path starting with // from becoming a network URL.
    return `/.${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

const drawImage = markdown.renderer.rules.image!
markdown.renderer.rules.image = (tokens, at, options, env, self) => {
  const token = tokens[at]!
  const local = (env as RenderOptions | undefined)?.localImagesOnly === true
  if (local) {
    const src = localImageSource(String(token.attrGet('src') ?? ''))
    if (src === null) token.attrs = (token.attrs ?? []).filter(([name]) => name !== 'src')
    else token.attrSet('src', src)
  }
  return drawImage(tokens, at, options, env, self)
}

/** GitHub's heading slug: lower case, punctuation and symbols dropped, spaces to hyphens. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}\p{Pc}\s-]/gu, '')
    .replace(/\s/g, '-')
}

function plainText(children: Token[]): string {
  let text = ''
  for (const child of children) {
    if (child.type === 'text' || child.type === 'code_inline') text += child.content
  }
  return text
}

function headingIds(state: StateCore): void {
  const used = new Map<string, number>()
  const tokens = state.tokens
  for (let i = 0; i < tokens.length; i++) {
    const open = tokens[i]!
    if (open.type !== 'heading_open') continue
    const inline = tokens[i + 1]
    const base = slugify(inline?.children ? plainText(inline.children) : (inline?.content ?? '')) || 'section'
    let slug = base
    let count = used.get(base)
    if (count !== undefined) {
      do slug = `${base}-${++count}`
      while (used.has(slug))
      used.set(base, count)
    }
    used.set(slug, used.get(slug) ?? 0)
    open.attrSet('id', slug)
  }
}

const CHECKBOX_HTML = '<input class="task-list-item-checkbox"'

/** Marks done items so they can be tinted (the plugin only marks the box). */
function taskItemState(state: StateCore): void {
  const tokens = state.tokens
  for (let i = 2; i < tokens.length; i++) {
    const inline = tokens[i]!
    const box = inline.type === 'inline' ? inline.children?.[0] : undefined
    if (!box || box.type !== 'html_inline' || !box.content.startsWith(CHECKBOX_HTML)) continue
    const item = tokens[i - 2]!
    if (item.type === 'list_item_open' && box.content.includes(' checked=""')) {
      item.attrJoin('class', 'task-list-item-checked')
    }
  }
}

type TokenClass = StateCore['Token']
type IssueLookup = ResolveIssue | undefined

function chipToken(TokenCtor: TokenClass, issue: IssueRef | undefined): Token | null {
  if (!issue) return null
  const slug = issue.state.replace(/[^a-z0-9_-]/gi, '')
  const chip = new TokenCtor('html_inline', '', 0)
  chip.content = `<span class="sun-chip sun-chip-${slug}">${escapeHtml(issue.stateName ?? stateName(issue.state))}</span>`
  return chip
}

function markIssueLink(open: Token, issue: IssueRef | undefined): void {
  open.attrJoin('class', 'sun-ref')
  if (issue?.title) open.attrSet('title', issue.title)
}

function issueLinkTokens(TokenCtor: TokenClass, key: string, resolve: IssueLookup): Token[] {
  const issue = resolve?.(key)
  const open = new TokenCtor('link_open', 'a', 1)
  open.attrs = [['href', `/${key}`]]
  markIssueLink(open, issue)
  const text = new TokenCtor('text', '', 0)
  text.content = key
  const close = new TokenCtor('link_close', 'a', -1)
  const chip = chipToken(TokenCtor, issue)
  return chip ? [open, text, close, chip] : [open, text, close]
}

function splitText(token: Token, TokenCtor: TokenClass, resolve: IssueLookup, prefixes: readonly string[]): Token[] {
  const matches = findIssueKeys(token.content, prefixes)
  if (matches.length === 0) return [token]
  const out: Token[] = []
  let at = 0
  for (const { index, length, key } of matches) {
    if (index > at) {
      const before = new TokenCtor('text', '', 0)
      before.content = token.content.slice(at, index)
      out.push(before)
    }
    // The link reads as the key is written, however the text spelled it (`sun-12` is SUN-012).
    out.push(...issueLinkTokens(TokenCtor, key, resolve))
    at = index + length
  }
  if (at < token.content.length) {
    const after = new TokenCtor('text', '', 0)
    after.content = token.content.slice(at)
    out.push(after)
  }
  return out
}

const FORMATTING = new Set(['strong_open', 'strong_close', 'em_open', 'em_close', 's_open', 's_close'])

/** The key a link's text spells exactly, in any spelling (`[SUN-5](…)`, `[**sun-005**](…)`), as it is written; or null. */
function linkTextKey(inner: Token[], prefixes: readonly string[]): string | null {
  let text = ''
  for (const token of inner) {
    if (token.type === 'text') text += token.content
    else if (!FORMATTING.has(token.type)) return null
  }
  return exactIssueKey(text, prefixes)
}

// Raw inline HTML that must keep its text untouched: links and code.
const RAW_OPEN = /^<(a|code|kbd|samp|pre)(?:\s[^>]*)?>$/i
const RAW_CLOSE = /^<\/(a|code|kbd|samp|pre)\s*>$/i

function linkInline(children: Token[], TokenCtor: TokenClass, resolve: IssueLookup, prefixes: readonly string[]): Token[] {
  const out: Token[] = []
  let rawDepth = 0
  for (let i = 0; i < children.length; i++) {
    const token = children[i]!
    if (token.type === 'link_open') {
      let close = i + 1
      while (close < children.length && children[close]!.type !== 'link_close') close++
      const inner = children.slice(i + 1, close)
      const key = linkTextKey(inner, prefixes)
      const href = String(token.attrGet('href') ?? '')
      if (key && issueKeyFromHref(href, prefixes) === key) {
        // A link that names an issue by its key is that issue, wherever the
        // URL came from (Linear, the hub's public URL, the app itself). An
        // in-app path keeps its query or fragment; anything else is rebased.
        const suffix = href.startsWith('/') ? (/[?#].*$/.exec(href)?.[0] ?? '') : ''
        const issue = resolve?.(key)
        token.attrSet('href', `/${key}${suffix}`)
        markIssueLink(token, issue)
        // Its text reads as the key is written, however the author spelled it (`[sun-5](…)` is SUN-005).
        let spelled = false
        for (const part of inner) {
          if (part.type !== 'text' || part.content === '') continue   // an empty text stands beside each mark of emphasis
          part.content = spelled ? '' : key
          spelled = true
        }
        out.push(token, ...inner)
        if (close < children.length) out.push(children[close]!)
        const chip = chipToken(TokenCtor, issue)
        if (chip) out.push(chip)
      } else {
        out.push(token, ...children.slice(i + 1, close + 1))
      }
      i = close
      continue
    }
    if (token.type === 'html_inline') {
      const html = token.content.trim()
      if (RAW_OPEN.test(html)) rawDepth++
      else if (RAW_CLOSE.test(html) && rawDepth > 0) rawDepth--
      out.push(token)
      continue
    }
    if (token.type === 'text' && rawDepth === 0) {
      out.push(...splitText(token, TokenCtor, resolve, prefixes))
      continue
    }
    out.push(token)
  }
  return out
}

function issueReferences(state: StateCore): void {
  const context = contextOf(state)
  const resolveIssue = context.resolveIssue
  const prefixes = context.prefixes ?? resolveIssue?.prefixes ?? []
  for (const block of state.tokens) {
    if (block.type !== 'inline' || !block.children) continue
    block.children = linkInline(block.children, state.Token, resolveIssue, prefixes)
  }
}


function importedFileLinks(state: StateCore): void {
  const hashes = contextOf(state).fileHashes
  if (!hashes?.size) return
  for (const block of state.tokens) for (const token of block.children ?? []) {
    if (token.type !== 'link_open') continue
    const href = String(token.attrGet('href') ?? '')
    if (!href.startsWith('/') && !/^https?:\/\//i.test(href)) continue
    try {
      const url = new URL(href, 'https://board.invalid')
      const hash = /^\/files\/([0-9a-f]{64})$/.exec(url.pathname)?.[1]
      if (hash && hashes.has(hash)) token.attrSet('href', `/files/${hash}`)
    } catch { /* An invalid URL stays as written. */ }
  }
}


/** The address after each link whose words are not that address (`linkTargets`). */
function linkTargets(state: StateCore): void {
  if (!contextOf(state).linkTargets) return
  for (const block of state.tokens) {
    if (block.type !== 'inline' || !block.children) continue
    const out: Token[] = []
    let target: string | null = null
    let words = ''
    for (const token of block.children) {
      out.push(token)
      if (token.type === 'link_open') {
        // An autolink or a linkified address shows itself; an issue's link shows its key and its title.
        const issue = String(token.attrGet('class') ?? '').split(' ').includes('sun-ref')
        target = token.info === 'auto' || issue ? null : String(token.attrGet('href') ?? '')
        words = ''
      } else if (token.type === 'text' || token.type === 'code_inline') {
        words += token.content
      } else if (token.type === 'link_close') {
        if (target !== null && words.trim() !== target) {
          const shown = new state.Token('text', '', 0)
          shown.content = ` (${target})`
          out.push(shown)
        }
        target = null
      }
    }
    block.children = out
  }
}


// ---------------------------------------------------------------- blocks

function fenceLanguage(info: string): string {
  const word = info.trim().split(/\s+/, 1)[0] ?? ''
  return word.replace(/^\{\.?|\}$/g, '')
}

/** The fences whose block is a command to run, as their author labelled them. */
const SHELL_LABELS: ReadonlySet<string> = new Set(['bash', 'sh', 'zsh'])

/**
 * A code block in its box: a head with the language as the author wrote it
 * (none without one). The view adds the buttons to the head; a shell block
 * is marked `md-code-shell` for its Run.
 */
function codeBlock(label: string, code: string, shell = false): string {
  const language = label ? `<span class="md-code-lang">${escapeHtml(label)}</span>` : ''
  return (
    `<div class="md-code-block${shell ? ' md-code-shell' : ''}"><div class="md-code-head">${language}</div>` +
    `<pre class="md-code">${code}</pre></div>\n`
  )
}

markdown.use(taskLists, { enabled: false })
markdown.core.ruler.push('poulpe_tasks', taskItemState)
markdown.core.ruler.push('poulpe_repo_urls', (state) => {
  const resolve = contextOf(state).resolveUrl
  if (!resolve) return
  for (const block of state.tokens) for (const token of block.children ?? []) {
    if (token.type === 'link_open') token.attrSet('href', resolve(String(token.attrGet('href') ?? ''), 'link'))
    if (token.type === 'image') token.attrSet('src', resolve(String(token.attrGet('src') ?? ''), 'image'))
  }
})
markdown.core.ruler.push('poulpe_heading_ids', headingIds)
markdown.core.ruler.push('poulpe_issue_refs', issueReferences)
markdown.core.ruler.push('poulpe_file_links', importedFileLinks)
markdown.core.ruler.push('poulpe_link_targets', linkTargets)

markdown.renderer.rules.fence = (tokens, idx) => {
  const token = tokens[idx]!
  const label = fenceLanguage(token.info ? markdown.utils.unescapeAll(token.info) : '')
  if (label.toLowerCase() === 'mermaid') {
    return `<div class="md-mermaid"><pre class="md-mermaid-source"><code>${escapeHtml(token.content)}</code></pre></div>\n`
  }
  const { html, language } = highlightCode(token.content, label)
  const languageClass = language ? ` language-${language.replace(/[^\w#+.-]/g, '')}` : ''
  return codeBlock(label, `<code class="hljs${languageClass}">${html}</code>`, SHELL_LABELS.has(label.toLowerCase()) && token.content.trim() !== '')
}
markdown.renderer.rules.code_block = (tokens, idx) => codeBlock('', `<code class="hljs">${escapeHtml(tokens[idx]!.content)}</code>`)

/** The block tokens `renderMarkdown` renders, heading ids included: the outline reads its headings here. */
export function parseMarkdown(source: string): Token[] {
  return markdown.parse(source, { [ENV_KEY]: {} })
}

export function renderMarkdown(source: string, options: RenderOptions = {}): string {
  // The image rule reads `localImagesOnly` from the env itself; the rest is the poulpe context.
  return sanitizeHtml(markdown.render(source, { ...options, [ENV_KEY]: options }))
}
